import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { TraceabilityApiClient } from '../api/TraceabilityApiClient';
import { useApiClient } from '@/hooks/useApiClient';
import { ToolIdentityStrip } from '../components/ToolIdentityStrip';
import { TraceabilityForm } from '../components/TraceabilityForm';
import { ProcessSelectionCard } from '../components/ProcessSelectionCard';
import { ProcessFilteredForm } from '../components/ProcessFilteredForm';
import { useProcessSelection } from '../hooks/useProcessSelection';
import { PageLayout } from '@/components/shared/PageLayout';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { Breadcrumb } from '@/components/shared/Breadcrumb';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card/Card';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/shared/EmptyState';

export function TraceabilityEntry() {
  const http = useApiClient();
  const [params] = useSearchParams();
  const toolId = params.get('toolId');
  const [showInformations, setShowInformations] = useState(true);
  const [toolIdValid, setToolIdValid] = useState<boolean>(true);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [companyName, setCompanyName] = useState<string>('');
  const [toolReference, setToolReference] = useState<string | null>(null);

  const traceabilityApiClient = useMemo(
    () => (http ? new TraceabilityApiClient(http) : null),
    [http]
  );

  const { processes, isLoading: processesLoading, error: processesError, selectedProcess, skipped, select, skip, reopen } =
    useProcessSelection(traceabilityApiClient, toolReference);

  const breadcrumbItems = [
    {
      label: t('common.brand'),
      onClick: () => navigate('/'),
    },
    {
      label: t('traceability.scanPage.hero.breadcrumb'),
      onClick: () => navigate('/traceability/scan'),
    },
    {
      label: t('traceability.formPage.hero.breadcrumb'),
    },
  ];

  const renderFormArea = () => {
    if (!toolIdValid || !toolId || !traceabilityApiClient) return null;

    // While fetching process templates, show skeleton in place of form
    if (processesLoading) {
      return <Skeleton className="h-20 rounded-xl" />;
    }

    // Process selected — show flat form
    if (selectedProcess !== null) {
      return (
        <ProcessFilteredForm
          process={selectedProcess}
          toolId={toolId}
          companyName={companyName}
          tracabilityApiClient={traceabilityApiClient}
          onChange={reopen}
          onClear={skip}
        />
      );
    }

    // Processes found, not yet skipped — show selection card (form suppressed)
    if (processes.length > 0 && !processesError && !skipped) {
      return (
        <ProcessSelectionCard
          processes={processes}
          isLoading={false}
          error={null}
          onSelect={select}
          onSkip={skip}
        />
      );
    }

    // No processes found, error, or explicit skip — normal 7-tab form
    return (
      <>
        {processesError && (
          <ProcessSelectionCard
            processes={[]}
            isLoading={false}
            error={processesError}
            onSelect={select}
            onSkip={skip}
          />
        )}
        <TraceabilityForm
          toolId={toolId}
          tracabilityApiClient={traceabilityApiClient}
          companyName={companyName}
          className="self-stretch"
        />
      </>
    );
  };

  return (
    <PageLayout contentClassName="max-w-7xl">
      <div className="flex flex-col gap-4">
        <Card as="header" tier="hero" padding="none" className="flex flex-col gap-3 px-6 py-4">
          <Breadcrumb items={breadcrumbItems} />
          {showInformations && toolId && traceabilityApiClient && (
            <ToolIdentityStrip
              toolId={toolId}
              traceabilityApiClient={traceabilityApiClient}
              setCompanyName={setCompanyName}
              onClose={() => setShowInformations(false)}
              onValid={setToolIdValid}
              setToolReference={setToolReference}
            />
          )}
        </Card>

        {toolIdValid === false && (
          <EmptyState
            tone="error"
            icon={AlertTriangle}
            title={t('traceability.formPage.invalid.title')}
            description={t('traceability.formPage.invalid.description')}
            action={
              <Button className="w-full sm:w-auto" onClick={() => navigate(-1)}>
                {t('traceability.formPage.invalid.backButton')}
              </Button>
            }
          />
        )}

        {renderFormArea()}
      </div>
    </PageLayout>
  );
}
